import { useCallback, useEffect, useState } from "react";
import { Button, Grid, Paper, Stack, Typography } from "@mui/material";
import "../../AppPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import UploadBreadCrumbs from "../../../components/upload/UploadBreadCrumbs";
import { humanFileSize } from "../../../store/formatUtil";
import UploadStatus from "../../../components/upload/UploadStatus";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "../../../store/config";
import { useCase } from "../../../store/case/hooks";
import { useClip } from "../../../store/clip/hooks";
import { useUploadQueue } from "../../../store/uploadQueue/hooks";
import { useAuth } from "../../../store/auth/hooks";

export default function AudioUploadPage() {
  // This page should handle the audio upload process, including through normalization,
  // rather than passing to the normalizing page when that step is hit
  const navigate = useNavigate();
  const { caseId, clipId } = useParams();
  const { case: af_case, loadCase } = useCase();
  const {
    reset: uploadQueueReset,
    startUpload: uploadQueueStartUpload,
    cancelUpload: uploadQueueCancelUpload,
    files,
    uploadState,
    overallProgress,
  } = useUploadQueue();
  const { loadClip, clip } = useClip();
  const { accessCode } = useAuth();

  const [normalizing, setNormalizing] = useState(false);
  const [normalizingPercent, setNormalizingPercent] = useState(0);
  const [breadcrumb, setBreadcrumb] = useState(1);
  const [websocketRegistered, setWebsocketRegistered] = useState(false);

  const ProgressText = () => {
    if (normalizing) {
      return (
        <Typography variant="h5">{normalizingPercent.toFixed(0)}%</Typography>
      );
    } else {
      if (overallProgress.toFixed(0) === "0") {
        return <Typography variant="h6">Reading From Drive</Typography>;
      }
      return (
        <Typography variant="h5">{overallProgress.toFixed(0)}%</Typography>
      );
    }
  };

  const load = useCallback(async () => {
    const { payload: loadedClip } = await loadClip(caseId, clipId);
    loadCase(caseId);
    console.log("Loaded clip for upload page", loadedClip);
    if (loadedClip.status === "WAITING_FOR_FILES" && files.length > 0) {
      uploadQueueStartUpload();
    } else {
      navigate(`/app/cases/${caseId}/clips/${clipId}`);
    }
  }, [
    caseId,
    clipId,
    files,
    loadClip,
    loadCase,
    uploadQueueStartUpload,
    navigate,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Setup a websocket
   */
  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event && event.data) {
        const data = JSON.parse(event.data);
        if (data.command === "CLIP_STATUS" && data.data.clipId === clip.id) {
          if (data.data.clipStatus === "EDITING") {
            navigate(`/app/cases/${caseId}/clips/`);
          }
          if (data.data.clipStatus === "NORMALIZING") {
            // switch to normalization mode
            setNormalizing(true);
            setBreadcrumb(2);
          }
        } else if (
          data.command === "NORMALIZATION_MESSAGE" &&
          data.data.clipId === clip.id
        ) {
          //Set State for total and completed files
          const percentComplete =
            (data.data.completedFiles / data.data.totalFiles) * 100;
          // check if percentComplete is NaN
          if (isNaN(percentComplete)) {
            console.log("NaN percent complete, resetting to 0");
            setNormalizingPercent(0);
          } else {
            setNormalizingPercent(percentComplete);
          }
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  const getSize = (files) => {
    let s = 0;

    for (let f of files) {
      s += f.size;
    }

    return humanFileSize(s, true, 2);
  };

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} gap={2} p={2}>
        <UploadBreadCrumbs active={breadcrumb} />
        <Paper elevation={4} sx={{ p: 2 }}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">STATUS</Typography>
              <UploadStatus state={uploadState} />
            </Grid>
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">AUDIO FILES</Typography>
              <Typography variant="h5">{files.length}</Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">IMPORT SIZE</Typography>
              <Typography variant="h5">{getSize(files)}</Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">PROGRESS</Typography>
              <ProgressText />
            </Grid>
          </Grid>
        </Paper>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={async () => {
            navigate(`/app/cases/${caseId}/clips`, { replace: true });
          }}
          sx={{ mr: 1 }}
        >
          Return to Clips
        </Button>
        <Button
          variant="outlined"
          onClick={async () => {
            await uploadQueueCancelUpload(caseId, clipId);
            await uploadQueueReset(clipId);
            navigate(`/app/cases/${caseId}/clips`, { replace: true });
          }}
        >
          CANCEL
        </Button>
      </Paper>
      <ContentTitleBar
        name={af_case.caseName}
        label={`${clip.description} Source Media`}
        onBack={async () => {
          await uploadQueueReset(clipId);
          navigate(`/app/cases/${caseId}/clips`, { replace: true });
        }}
      />
    </Stack>
  );
}
